import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_BASE_URL } from '../../config';
import { GoogleMap, Polyline as GooglePolyline } from '@react-google-maps/api';
import polyline from '@mapbox/polyline';
import './ActivityList.css';

interface Activity {
  id: number;
  strava_activity_id: number;
  name: string;
  type: string;
  distance: number;
  moving_time: number;
  elapsed_time: number;
  total_elevation_gain: number;
  start_date: string;
  start_date_local: string;
  average_speed: number;
  max_speed: number;
  average_heartrate?: number;
  max_heartrate?: number;
  calories: number;
  polyline?: string;
}

interface ActivityListProps {
  isLoaded: boolean;
}

const ActivityList: React.FC<ActivityListProps> = ({ isLoaded }) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('all');
  const [mapModalActivity, setMapModalActivity] = useState<Activity | null>(null);
  const { t } = useLanguage();

  const checkAuthStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/status`, {
        credentials: 'include'
      });
      const data = await response.json();
      console.log('Auth status:', data);
      
      if (!data.authenticated) {
        setError('Sesja wygasła. Zaloguj się ponownie.');
        return false;
      }
      return true;
    } catch (error) {
      console.error('Auth check failed:', error);
      setError('Problem z autoryzacją');
      return false;
    }
  };

  const fetchWithRetry = async (url: string, options: RequestInit, retries = 3): Promise<Response> => {
    for (let i = 0; i < retries; i++) {
      try {
        console.log('Fetch attempt', i + 1, 'URL:', url, 'Options:', options);
        const response = await fetch(url, {
          ...options,
          credentials: 'include',
        });
        console.log('Fetch response:', response);
        return response;
      } catch (error) {
        console.error('Fetch error:', error);
        if (i === retries - 1) throw error;
        await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
      }
    }
    throw new Error('All retries failed');
  };

  useEffect(() => {
    console.log('User Agent:', navigator.userAgent);
    console.log('Cookies enabled:', navigator.cookieEnabled);
    console.log('API Base URL:', API_BASE_URL);
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    const isAuth = await checkAuthStatus();
    if (!isAuth) return;
    
    try {
      console.log('Pobieranie aktywności z:', `${API_BASE_URL}/api/activities`);
      const response = await fetchWithRetry(`${API_BASE_URL}/api/activities`, {});
      
      console.log('Status odpowiedzi:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Otrzymane dane:', data);
        setActivities(data.activities);
      } else {
        const errorData = await response.json();
        console.error('Błąd API:', errorData);
        setError(errorData.error || 'Błąd podczas pobierania aktywności');
      }
    } catch (error) {
      console.error('Błąd połączenia:', error);
      setError('Błąd połączenia z serwerem');
    } finally {
      setLoading(false);
    }
  };

  const formatDistance = (distance: number) => {
    return (distance / 1000).toFixed(1) + ' km';
  };

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const formatSpeed = (speed: number) => {
    return (speed * 3.6).toFixed(1) + ' km/h';
  };

  const formatPace = (metersPerSecond: number) => {
    if (metersPerSecond <= 0) return '—';
    const secPerKm = 1000 / metersPerSecond;
    const min = Math.floor(secPerKm / 60);
    const sec = Math.floor(secPerKm % 60);
    return `${min}:${sec.toString().padStart(2, '0')} /km`;
  };

  const formatSwimPace = (metersPerSecond: number) => {
    if (metersPerSecond <= 0) return '—';
    const secPer100 = 100 / metersPerSecond;
    const min = Math.floor(secPer100 / 60);
    const sec = Math.floor(secPer100 % 60);
    return `${min}:${sec.toString().padStart(2, '0')} /100m`;
  };

  const CYCLING_TYPES = ['ride', 'virtualride', 'mountainbikeride', 'ebikeride', 'handcycle', 'velomobile'];
  const RUNNING_TYPES = ['run', 'virtualrun', 'trailrun'];
  const WALK_HIKE_TYPES = ['walk', 'hike'];
  const DISTANCE_TYPES = [...CYCLING_TYPES, ...RUNNING_TYPES, 'swim', ...WALK_HIKE_TYPES,
    'nordicski', 'alpineski', 'backcountryski', 'iceskate', 'inlineskate',
    'kayaking', 'kitesurf', 'rowing', 'skateboard', 'snowboard',
    'surfing', 'windsurf', 'canoeing', 'standuppaddling'];

  const getActivityStats = (activity: Activity): { label: string; value: string }[] => {
    const t_ = activity.type.toLowerCase();
    if (CYCLING_TYPES.includes(t_)) {
      return [
        { label: t('activities.distance'), value: formatDistance(activity.distance) },
        { label: t('activities.movingTime'), value: formatTime(activity.moving_time) },
        { label: t('activities.elevationGain'), value: `+${Math.round(activity.total_elevation_gain)}m` },
        { label: t('activities.averageSpeed'), value: formatSpeed(activity.average_speed) },
      ];
    }
    if (RUNNING_TYPES.includes(t_)) {
      const stats = [
        { label: t('activities.distance'), value: formatDistance(activity.distance) },
        { label: t('activities.movingTime'), value: formatTime(activity.moving_time) },
        { label: 'Pace', value: formatPace(activity.average_speed) },
      ];
      if (activity.total_elevation_gain > 50) {
        stats.push({ label: t('activities.elevationGain'), value: `+${Math.round(activity.total_elevation_gain)}m` });
      }
      return stats;
    }
    if (t_ === 'swim') {
      return [
        { label: t('activities.distance'), value: formatDistance(activity.distance) },
        { label: t('activities.movingTime'), value: formatTime(activity.moving_time) },
        { label: 'Pace', value: formatSwimPace(activity.average_speed) },
      ];
    }
    if (WALK_HIKE_TYPES.includes(t_)) {
      return [
        { label: t('activities.distance'), value: formatDistance(activity.distance) },
        { label: t('activities.movingTime'), value: formatTime(activity.moving_time) },
        { label: t('activities.elevationGain'), value: `+${Math.round(activity.total_elevation_gain)}m` },
      ];
    }
    if (DISTANCE_TYPES.includes(t_)) {
      return [
        { label: t('activities.distance'), value: formatDistance(activity.distance) },
        { label: t('activities.movingTime'), value: formatTime(activity.moving_time) },
        { label: t('activities.elevationGain'), value: `+${Math.round(activity.total_elevation_gain)}m` },
      ];
    }
    // Indoor / gym / strength / yoga
    const stats = [
      { label: t('activities.movingTime'), value: formatTime(activity.moving_time) },
    ];
    if (activity.calories > 0) {
      stats.push({ label: 'Calories', value: `${Math.round(activity.calories)} kcal` });
    }
    if (activity.average_heartrate && activity.average_heartrate > 0) {
      stats.push({ label: 'Avg HR', value: `${Math.round(activity.average_heartrate)} bpm` });
    }
    return stats;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredActivities = activities.filter(activity => {
    if (filter === 'all') return true;
    if (filter === 'ride') return activity.type === 'Ride';
    if (filter === 'virtualRide') return activity.type === 'VirtualRide';
    if (filter === 'mountainBikeRide') return activity.type === 'MountainBikeRide';
    return true;
  });

  const renderMap = (polylineStr: string) => {
    if (!isLoaded) {
      return <div>Loading map...</div>;
    }

    if (!polylineStr || polylineStr.trim() === '') {
      return <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>No route data available</div>;
    }

    let path: { lat: number; lng: number }[] = [];
    try {
      const decoded = polyline.decode(polylineStr);
      if (!decoded || !Array.isArray(decoded) || decoded.length < 2) {
        return <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>No route data available</div>;
      }

      // Filter out invalid points and validate coordinates
      path = decoded
        .filter((point): point is [number, number] => {
          if (!Array.isArray(point) || point.length < 2) return false;
          const [lat, lng] = point;
          return typeof lat === 'number' && typeof lng === 'number' &&
                 !isNaN(lat) && !isNaN(lng) &&
                 isFinite(lat) && isFinite(lng) &&
                 lat >= -90 && lat <= 90 &&
                 lng >= -180 && lng <= 180;
        })
        .map((point) => ({
          lat: point[0],
          lng: point[1]
        }));
    } catch (error) {
      console.error('Error decoding polyline:', error);
      return <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>Unable to load route</div>;
    }

    if (path.length < 2) {
      return <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>No route data available</div>;
    }

    const center = path[Math.floor(path.length / 2)];

    // Verify center is valid
    if (!center || typeof center.lat !== 'number' || typeof center.lng !== 'number') {
      return <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>Unable to display map</div>;
    }

    // Generate unique key from path to force clean re-mount
    const pathKey = `polyline-${path.length}-${path[0]?.lat}-${path[0]?.lng}`;

    return (
      <div style={{ height: '100%', minHeight: '300px' }}>
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={center}
          zoom={13}
        >
          {path.length >= 2 && (
            <GooglePolyline
              key={pathKey}
              path={path}
              options={{ strokeColor: '#FF0000', strokeWeight: 4 }}
            />
          )}
        </GoogleMap>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="card">
        <h2>{t('activities.title')}</h2>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>{t('activities.loadingActivities')}</p>
        </div>
      </div>
    );
  }

  if (error && error.toLowerCase().includes('strava')) {
    return (
      <div className="card" style={{ textAlign: 'center', background: '#f8fafc', marginTop: 30, padding: 32 }}>
        <h2>{t('strava.connect')}</h2>
        <p style={{ fontSize: '1.1em', margin: '18px 0 28px 0' }}>
          {t('strava.connectInfo')}
        </p>
        <button
          className="btn btn-primary"
          style={{ fontSize: '1.1em', padding: '14px 0', width: '100%', maxWidth: 400, margin: '0 auto', background: '#fc4c02', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600 }}
          onClick={() => { window.location.href = `${API_BASE_URL}/auth/strava`; }}
        >
          {t('strava.connect')}
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <h2>{t('activities.title')}</h2>
        <div style={{ 
          padding: '20px', 
          backgroundColor: 'rgba(248,215,218,0.8)', 
          color: '#721c24', 
          border: '1px solid #f5c6cb',
          borderRadius: '5px'
        }}>
          <p><strong>{t('activities.error')}:</strong> {error}</p>
          {(error.includes('autoryzacją') || error.includes('sesja wygasła')) && (
            <p style={{ marginTop: '10px', fontSize: '14px' }}>
              Problem z sesją. Spróbuj odświeżyć stronę lub zaloguj się ponownie.
            </p>
          )}
          <button onClick={fetchActivities} className="btn" style={{ marginTop: '10px' }}>
            {t('activities.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="activity-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
        <h2>{t('activities.title')}</h2>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <select 
            value={filter} 
            onChange={(e) => setFilter(e.target.value)}
            style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
          >
            <option value="all">{t('activities.all')}</option>
            <option value="ride">{t('activities.ride')}</option>
            <option value="virtualRide">{t('activities.virtualRide')}</option>
            <option value="mountainBikeRide">{t('activities.mountainBikeRide')}</option>
          </select>
          <button onClick={fetchActivities} className="btn" style={{ fontSize: '14px', padding: '8px 16px' }}>
            {t('activities.refresh')}
          </button>
        </div>
      </div>

      {activities.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          <p>{t('activities.noActivitiesToDisplay')}</p>
          <p>{t('activities.connectAccount')}</p>
          <div style={{ marginTop: '20px', padding: '15px', backgroundColor: 'rgba(248,249,250,0.8)', borderRadius: '5px' }}>
            <p style={{ margin: '0 0 10px 0', fontSize: '14px' }}>
              <strong>Wskazówki:</strong>
            </p>
            <ul style={{ textAlign: 'left', margin: '0', paddingLeft: '20px', fontSize: '14px' }}>
              <li>Upewnij się, że jesteś połączony ze Strava w zakładce "Profil"</li>
              <li>Sprawdź, czy masz aktywności rowerowe w Strava</li>
              <li>Spróbuj odświeżyć stronę</li>
            </ul>
          </div>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '20px'
        }} className="activities-grid">
          {filteredActivities.map((activity) => (
            <div key={activity.id} className="card activity-tile" style={{
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              height: '100%'
            }}>
              {/* Activity type badge */}
              <div style={{
                padding: '4px 10px',
                backgroundColor: activity.type === 'Ride' ? 'var(--primary-700)' : activity.type === 'VirtualRide' ? 'var(--primary-600)' : activity.type === 'MountainBikeRide' ? 'var(--primary-800)' : 'var(--primary-500)',
                color: '#fff',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: 'bold',
                alignSelf: 'flex-start',
                marginBottom: '10px'
              }}>
                {activity.type === 'Ride' ? '🚴' : activity.type === 'VirtualRide' ? '🖥️' : activity.type === 'MountainBikeRide' ? '🚵' : '🏃'} {activity.type}
              </div>

              {/* Title and date */}
              <h3 style={{
                margin: '0 0 4px 0',
                color: 'var(--text-primary)',
                fontSize: '14px',
                fontWeight: '600',
                lineHeight: '1.3',
                minHeight: '36px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}>{activity.name}</h3>
              <p style={{ margin: '0 0 12px 0', color: 'var(--text-secondary)', fontSize: '12px' }}>
                {formatDate(activity.start_date_local)}
              </p>

              {/* Stats grid (type-aware) */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '8px',
                marginBottom: '12px'
              }}>
                {getActivityStats(activity).map((stat, idx) => (
                  <div key={idx} style={{ textAlign: 'center', padding: '8px', background: 'var(--bg-secondary)', borderRadius: '6px' }}>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                      {stat.value}
                    </div>
                    <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* Heart rate row */}
              {(activity.average_heartrate || activity.max_heartrate) && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '12px',
                  padding: '6px',
                  background: 'var(--bg-secondary)',
                  borderRadius: '6px',
                  fontSize: '11px',
                  marginBottom: '12px'
                }}>
                  {activity.average_heartrate && (
                    <span>❤️ {activity.average_heartrate} bpm</span>
                  )}
                  {activity.max_heartrate && (
                    <span>🔥 {activity.max_heartrate} bpm</span>
                  )}
                </div>
              )}

              {/* Spacer to push buttons to bottom */}
              <div style={{ flexGrow: 1 }} />

              {/* Action buttons */}
              <div className="activity-buttons" style={{ display: 'flex', gap: '8px' }}>
                {shouldShowMap(activity.type) && activity.polyline && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setMapModalActivity(activity);
                    }}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      backgroundColor: 'var(--primary-700)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    {t('activities.viewMap')}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {filteredActivities.length > 0 && (
        <div style={{
          marginTop: '20px',
          padding: '15px',
          backgroundColor: 'rgba(231,243,255,0.8)',
          border: '1px solid #b3d9ff',
          borderRadius: '5px',
          textAlign: 'center'
        }}>
          <p style={{ margin: 0, color: '#0066cc' }}>
            <strong>{t('activities.displayedActivities')} {filteredActivities.length}</strong>
          </p>
        </div>
      )}

      {/* Map Modal */}
      {mapModalActivity && mapModalActivity.polyline && (
        <div
          className="map-modal-overlay"
          onClick={() => setMapModalActivity(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
        >
          <div
            className="map-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--card-bg)',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '800px',
              maxHeight: '90vh',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
          >
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              borderBottom: '1px solid var(--border-color)'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', color: 'var(--text-primary)' }}>
                  {mapModalActivity.name}
                </h3>
                <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
                  {getActivityStats(mapModalActivity).map(s => s.value).join(' • ')}
                </p>
              </div>
              <button
                onClick={() => setMapModalActivity(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'var(--text-secondary)',
                  padding: '4px 8px',
                  lineHeight: 1
                }}
              >
                ×
              </button>
            </div>

            {/* Map */}
            <div style={{ height: '500px' }}>
              {renderMap(mapModalActivity.polyline)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const shouldShowMap = (activityType: string) => {
  const t = activityType.toLowerCase();
  const indoorTypes = ['workout', 'weighttraining', 'crossfit', 'yoga', 'squash', 'tennis', 'basketball', 'soccer', 'football'];
  return !indoorTypes.includes(t);
};

export default ActivityList; 